import React, { useEffect, useCallback } from 'react'
import { TouchableOpacity } from 'react-native'
import { useSharedValue, useAnimatedStyle } from 'react-native-reanimated'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import { ToastProps } from './types'
import {
  ToastContainer,
  ToastContent,
  ToastColumn,
  ToastTitle,
  ToastDescription,
  ToastLink,
} from './style'
import { TOAST_CONSTANTS } from './constants'
import { createDismissAnimation, createEntryAnimation, createBounceAnimation } from './animations'
import { isWeb } from 'APP/Helpers/checkPlatform'

export const Toast: React.FC<ToastProps> = ({ data, onDismiss, onLinkPress }) => {
  const translateY = useSharedValue<number>(TOAST_CONSTANTS.POSITION.HIDDEN)
  const opacity = useSharedValue<number>(0)
  const gestureStartY = useSharedValue<number>(0)

  const handleDismiss = useCallback(() => {
    onDismiss(data.id)
  }, [data.id, onDismiss])

  useEffect(() => {
    createEntryAnimation(translateY, opacity)

    const dismissDelay = data.delay ?? TOAST_CONSTANTS.AUTO_DISMISS_DELAY
    const timer = setTimeout(() => {
      createDismissAnimation(translateY, opacity, handleDismiss)
    }, dismissDelay)

    return () => clearTimeout(timer)
  }, [data.id, data.delay, handleDismiss, opacity, translateY])

  const panGesture = Gesture.Pan()
    .withTestId('TOAST_PAN_GESTURE')
    .onStart(() => {
      gestureStartY.value = translateY.value
    })
    .onUpdate((event) => {
      if (event.translationY < 0) {
        translateY.value = gestureStartY.value + event.translationY
      }
    })
    .onEnd((event) => {
      if (event.translationY < TOAST_CONSTANTS.SWIPE_THRESHOLD) {
        createDismissAnimation(translateY, opacity, handleDismiss)
      } else {
        createBounceAnimation(translateY)
      }
    })

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    opacity: opacity.value,
  }))

  const handlePress = useCallback(() => {
    if (data.link) {
      onLinkPress(data.link.url, data.link.isExternal)
      onDismiss(data.id)
    }
  }, [data.id, data.link, onDismiss, onLinkPress])

  const accessibilityLabel = data.description
    ? `${data.title}. ${data.description}${data.link ? `. ${data.link.text}` : ''}`
    : data.title

  const toastContent = (
    <ToastContainer
      testID="TOAST_CONTAINER"
      style={animatedStyle}
      hideMenuWebMargin={data.hideMenuWebMargin}
      variant={data.variant}
      isWeb={isWeb()}
      accessibilityRole="alert"
      accessibilityLiveRegion="assertive"
      accessibilityLabel={accessibilityLabel}
      tabIndex={isWeb() ? -1 : undefined}
      role={isWeb() ? 'alert' : undefined}
      aria-live={isWeb() ? 'assertive' : undefined}
      aria-atomic={isWeb() ? true : undefined}
    >
      <TouchableOpacity
        onPress={handlePress}
        activeOpacity={
          data.link ? TOAST_CONSTANTS.OPACITY.LINK_PRESS : TOAST_CONSTANTS.OPACITY.NO_LINK
        }
        disabled={!data.link}
        accessibilityRole={isWeb() ? undefined : 'button'}
      >
        <ToastContent isWeb={isWeb()}>
          <ToastColumn>
            <ToastTitle isWeb={isWeb()} toastVariant={data.variant}>
              {data.title}
            </ToastTitle>
            {data.description && (
              <ToastDescription isWeb={isWeb()} toastVariant={data.variant}>
                {data.description}
              </ToastDescription>
            )}
          </ToastColumn>
          {data.link && (
            <ToastLink isWeb={isWeb()} toastVariant={data.variant}>
              {data.link.text}
            </ToastLink>
          )}
        </ToastContent>
      </TouchableOpacity>
    </ToastContainer>
  )

  if (isWeb()) {
    return toastContent
  }

  return <GestureDetector gesture={panGesture}>{toastContent}</GestureDetector>
}

export default Toast
